import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import type { ICellEditorParams } from 'ag-grid-community';

const CLEAR_SENTINEL = '__SLT_CLEAR__';

type Primitive = string | number | boolean | null | undefined;

export type CheckboxGroupNativeSelectCellEditorParams = ICellEditorParams & {
  values?: Primitive[];
  formatValue?: (value: unknown) => string;
};

function serializeOption(v: Primitive): string {
  if (v === null || typeof v === 'undefined') {
    return CLEAR_SENTINEL;
  }
  if (typeof v === 'boolean') {
    return v ? 'true' : 'false';
  }
  return String(v);
}

/**
 * In-cell native select for checkbox-group Value columns. Avoids agRichSelect popup/focus issues
 * (dropdown disappearing) in grouped/edit grids wrapped by React shells.
 */
const CheckboxGroupNativeSelectCellEditor = forwardRef<
  unknown,
  CheckboxGroupNativeSelectCellEditorParams
>((props, ref) => {
  const {
    stopEditing,
    value,
    values = [],
    formatValue = v => (v == null ? '' : String(v)),
    node,
    colDef,
    column,
  } = props;
  const selectRef = useRef<HTMLSelectElement>(null);
  const valueRef = useRef<Primitive>(value ?? null);
  const didTryOpenListRef = useRef(false);

  const resolveSelectedKey = (currentValue: Primitive) => {
    const currentSerialized = serializeOption(currentValue);

    return values.some(v => serializeOption(v) === currentSerialized)
      ? currentSerialized
      : serializeOption(values[0]);
  };

  const [selectedKey, setSelectedKey] = useState(() => resolveSelectedKey(value as Primitive));

  const openSelectList = (el: HTMLSelectElement) => {
    el.focus();
    try {
      const sel = el as HTMLSelectElement & { showPicker?: () => void };
      if (typeof sel.showPicker === 'function') {
        sel.showPicker();
        return;
      }
    } catch {
      // showPicker can throw if the browser blocks it
    }
    el.click();
  };

  const tryOpenSelectListOnce = () => {
    if (didTryOpenListRef.current) {
      return;
    }
    const el = selectRef.current;
    if (!el) {
      return;
    }
    didTryOpenListRef.current = true;
    // Layout effect runs right after mount; afterGuiAttached is a fallback if ref was not ready.
    openSelectList(el);
  };

  useLayoutEffect(() => {
    tryOpenSelectListOnce();
  }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => valueRef.current,
    afterGuiAttached: () => {
      tryOpenSelectListOnce();
    },
    isPopup: () => false,
  }));

  if (!values.length) {
    return <span className="slt-checkbox-group-native-select-empty" />;
  }

  return (
    <select
      ref={selectRef}
      className="slt-checkbox-group-native-select"
      style={{ width: '100%', height: '100%', minHeight: 28, boxSizing: 'border-box' as const }}
      value={selectedKey}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
      onChange={event => {
        const raw = event.target.value;
        if (raw === CLEAR_SENTINEL) {
          valueRef.current = null;
        } else {
          const match = values.find(v => serializeOption(v) === raw);
          valueRef.current = match !== undefined ? match : raw;
        }

        setSelectedKey(raw);

        const field = colDef?.field || column?.getColId();
        if (node && field) {
          node.setDataValue(field, valueRef.current);
        }

        stopEditing();
      }}
    >
      {values.map((v, i) => (
        <option key={`opt-${i}-${serializeOption(v)}`} value={serializeOption(v)}>
          {formatValue(v)}
        </option>
      ))}
    </select>
  );
});

CheckboxGroupNativeSelectCellEditor.displayName = 'CheckboxGroupNativeSelectCellEditor';

export default CheckboxGroupNativeSelectCellEditor;
